import React from 'react';
import styled, { css } from 'styled-components';
import {
  Banner as BannerLib,
  BannerVariant,
  Box,
} from '@sparknz/spark-pattern-library';
import { LinkRegion } from '@sparknz/tracking';

import extractCQStyle, { CQStyleMap } from 'shared/utils/extractCQStyle';
import {
  Actions,
  BannerEditConfig,
  BannerProps,
  LibActions,
  LibBannerActions,
} from './BannerEditConfig';

const bannerStyleCQStyleMap: CQStyleMap<boolean> = {
  'banner-style--dark-text': true,
  'banner-style--light-text': false,
};

const bannerTitleSizeCQStyleMap: CQStyleMap<string> = {
  'banner-title-size--h2': 'h2',
  'banner-title-size--h1': 'h1',
};

const bannerGradientOverlayCQStyleMap: CQStyleMap<boolean> = {
  'banner-gradient-overlay': true,
};

const extendedBackgroundCQStyleMap: CQStyleMap<boolean> = {
  'extend-full-length-mobile': true,
};

const roundedCornersCQStyleMap: CQStyleMap<boolean> = {
  'rounded-corner': true,
};

const imageOnLeftCQStyleMap: CQStyleMap<boolean> = {
  'image-on-left': true,
};

const EditorModeWrapper = styled(Box)`
  ${({ theme: { breakpoints } }) => css`
    @media (max-width: ${breakpoints.lil}px) {
      height: ${window.innerHeight - 80}px;
      overflow-y: auto;
    }
  `}
`;

interface HandleEditorHeightProps {
  isInEditor: boolean;
  variant: string;
}

const HandleEditorHeight = ({
  children,
  isInEditor,
  variant,
}: React.PropsWithChildren<HandleEditorHeightProps>) => {
  if (isInEditor && variant === BannerVariant.CURRENT) {
    return <EditorModeWrapper>{children}</EditorModeWrapper>;
  }

  return <>{children}</>;
};

const Banner = (props: BannerProps) => {
  if (BannerEditConfig.isEmpty(props)) {
    return null;
  }

  const {
    isInEditor,
    title,
    description,
    subDescription,
    imagePath,
    bgImagePath,
    bgImagePathTablet,
    bgImagePathMobile,
    appliedCssClassNames: cssClassNames,
    regionId = 'banner',
    variant = BannerVariant.LEGACY as string,
  } = props;

  const bannerStyle =
    extractCQStyle(bannerStyleCQStyleMap, cssClassNames) || false;

  const bannerTitleSize =
    extractCQStyle(bannerTitleSizeCQStyleMap, cssClassNames) || false;

  const bannerGradientOverlay =
    extractCQStyle(bannerGradientOverlayCQStyleMap, cssClassNames) || false;

  const extendedBackground =
    extractCQStyle(extendedBackgroundCQStyleMap, cssClassNames) || false;

  const roundedCorners =
    extractCQStyle(roundedCornersCQStyleMap, cssClassNames) || false;

  const imageOnLeft =
    extractCQStyle(imageOnLeftCQStyleMap, cssClassNames) || false;

  const data: LibBannerActions = {
    actions: props.actions?.map(
      (actionItem: Actions): LibActions => ({
        title: actionItem.title,
        target: actionItem.link?.attributes?.target,
        url: actionItem.link?.url,
      }),
    ),
  };

  return (
    <LinkRegion regionId={regionId}>
      <HandleEditorHeight isInEditor={isInEditor} variant={variant}>
        <BannerLib
          title={title}
          description={description}
          subDescription={subDescription}
          imagePath={imagePath}
          bgImagePath={bgImagePath}
          bgImagePathTablet={bgImagePathTablet}
          bgImagePathMobile={bgImagePathMobile}
          actions={data.actions}
          hasDarkText={bannerStyle}
          titleSize={bannerTitleSize}
          hasGradientOverlay={bannerGradientOverlay}
          hasExtendedBackground={extendedBackground}
          hasRoundedCorners={roundedCorners}
          hasImageOnLeft={imageOnLeft}
          variant={variant}
        />
      </HandleEditorHeight>
    </LinkRegion>
  );
};

export default Banner;
